import React, { createContext, useContext, useEffect, useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { bindIdpClient, createIdpClient, type IdpClient } from '@digiwedge/idp-client';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { readEnvVar } from '../../env.runtime.js';

const IdpContext = createContext<IdpClient | null>(null);
type IdpAudience = ReturnType<NonNullable<Parameters<typeof createIdpClient>[0]['getAudience']>>;
const IDP_AUDIENCES: readonly IdpAudience[] = [
  'teetime-web',
  'teetime-admin',
  'billing-admin',
  'control-admin',
  'messaging-admin',
  'cmp-portal',
  'voicepro-web',
  'scl-admin',
  'facilities-admin',
  'yolo-admin',
];

function resolveIdpAudience(value: string | undefined): IdpAudience | null {
  const candidate = value?.trim();
  return IDP_AUDIENCES.includes(candidate as IdpAudience) ? (candidate as IdpAudience) : null;
}

export function IdpProvider({ children }: PropsWithChildren) {
  const token = useGlobalStore((s: GlobalState) => s.token);
  const refreshToken = useGlobalStore((s: GlobalState) => s.refreshToken);
  const jti = useGlobalStore((s: GlobalState) => s.jti);
  const login = useGlobalStore((s: GlobalState) => s.login);
  const logout = useGlobalStore((s: GlobalState) => s.logout);

  const basePath = readEnvVar('VITE_IDP_BASE_URL');
  // Default to same-origin when not configured to avoid cross-origin fallbacks.
  const effectiveBasePath = basePath === undefined ? '/' : basePath;

  // Resolve audience with this precedence:
  // 1) <meta name="idp-audience" content="..."> (runtime editable)
  // 2) VITE_IDP_AUDIENCE environment variable (build-time)
  // 3) default 'control-admin' (Access Control Admin default)
  const audience: IdpAudience = (() => {
    try {
      const meta = document.querySelector('meta[name="idp-audience"]') as HTMLMetaElement | null;
      const fromMeta = resolveIdpAudience(meta?.content);
      if (fromMeta) return fromMeta;
    } catch {
      // ignore if document not available in test env
    }
    try {
      const fromGlobal = resolveIdpAudience(readEnvVar('VITE_IDP_AUDIENCE'));
      if (fromGlobal) return fromGlobal;
    } catch {
      /* ignore */
    }
    const fromEnv = resolveIdpAudience(
      typeof process !== 'undefined' ? process.env?.['VITE_IDP_AUDIENCE'] : undefined,
    );
    if (fromEnv) return fromEnv;
    return 'control-admin';
  })();

  const client = useMemo(() => {
    const c = createIdpClient({
      basePath: effectiveBasePath,
      getAccessToken: () => useGlobalStore.getState().token ?? '',
      getRefreshToken: () => useGlobalStore.getState().refreshToken ?? '',
      getJti: () => useGlobalStore.getState().jti ?? '',
      login,
      logout,
      // Audience is parameterized via meta/env (see resolver above)
      getAudience: () => audience,
    });
    // Ensure hooks-auth-web uses the same basePath/axios as this provider.
    bindIdpClient(c);
    return c;
    // We intentionally do not include login/logout in deps to keep the client stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath, audience]);

  // Fetch CSRF cookie once so login/refresh/logout can use double-submit
  useEffect(() => {
    try {
      void client.axios.get('/api/auth/csrf');
    } catch {
      // ignore
    }
  }, [client]);

  // Keep lints happy: read values to re-render provider when tokens change
  void token;
  void refreshToken;
  void jti;

  return <IdpContext.Provider value={client}>{children}</IdpContext.Provider>;
}

export function useIdp() {
  const ctx = useContext(IdpContext);
  if (!ctx) throw new Error('useIdp must be used within IdpProvider');
  return ctx;
}
